import { supabase } from "@/integrations/supabase/client";

/**
 * Handles Google OAuth sign-in using full-page redirect
 * 
 * This is the industry-standard approach used by top SaaS companies (Stripe, Linear, Vercel, etc.)
 * because it's:
 * - More reliable (no popup blockers)
 * - More professional (no weird popup windows)
 * - Consistent across all browsers and devices
 * - Simpler and more maintainable
 * - Fast and snappy (no polling, no delays)
 * 
 * Flow:
 * 1. User clicks sign in → redirects to Google OAuth
 * 2. User authenticates → Google redirects back to /dashboard with auth code
 * 3. Supabase automatically exchanges code for session
 * 4. DashboardLayout detects session and loads user data
 * 
 * This is bulletproof because:
 * - Supabase handles all OAuth complexity
 * - No custom polling or timing logic
 * - Works on all devices/browsers identically
 * - Handles errors gracefully
 */
export async function signInWithGoogle(): Promise<void> {
  try {
    // Get the current origin to ensure we redirect back to the same domain
    const currentOrigin = window.location.origin;
    const redirectTo = `${currentOrigin}/dashboard`;
    
    console.log('Initiating Google sign-in with redirectTo:', redirectTo);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo,
        skipBrowserRedirect: true, // We'll manually redirect for better control
        queryParams: {
          // Ensure we get refresh tokens for better session management
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    
    if (error) {
      console.error('Error initiating Google sign-in:', error);
      throw error;
    }
    
    // Manually redirect to the OAuth URL
    // This ensures the redirect always happens, even if Supabase's automatic redirect fails
    if (data?.url) {
      console.log('Redirecting to:', data.url);
      // Use replace() instead of href for more forceful redirect
      // This prevents the current page from staying in history
      // The redirect happens synchronously - execution stops here
      window.location.replace(data.url);
      // This line should never execute, but TypeScript requires a return
      return;
    } else {
      const errorMsg = 'No redirect URL returned from Supabase. Please check your Supabase configuration.';
      console.error(errorMsg);
      console.error('Supabase response data:', data);
      throw new Error(errorMsg);
    }
    
  } catch (error) {
    console.error('Sign-in error:', error);
    throw error;
  }
}

